import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { tripStart } from "../../src/days.ts";
import { api, type Item, type Person, type TripView } from "./api.ts";
import {
  ANSWERS,
  type Answer,
  type AreaSummary,
  answer,
  areaSummary,
  type DeckArea,
  type DeckRun,
  deckShows,
  fromStay,
  giveBack,
  isRun,
  saved,
  startRun,
  swipeAnswer,
  takeBack,
  unanswer,
  wantItToo,
  wanting,
  wantsTooText,
} from "./deck.ts";
import { itemKind, km, originLabel, picksLabel, ratingLabel } from "./format.ts";
import { haptic } from "./haptics.ts";
import { useMedia } from "./hooks.ts";
import { ItemCover, ItemThumb } from "./Photos.tsx";
import { MEANS, Ratings } from "./Rate.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { rememberInStep, stepState } from "./router.ts";
import { AvatarStack, Button, Icon } from "./souvenir.ts";
import { factLine } from "./ui.tsx";

// Rating one at a time, the deck (DESIGN.md, Views, "Rating one at a time"; features/interest.md): what
// waits on my rating, an area at a time in the order of the trip's stays, one card at a time. Swiping
// right is Yes, left No, and up or down springs back; the buttons give all three (No, Maybe, Yes,
// drawn alike, none filled) and Later; Undo takes back the last. On a computer,
// the arrow keys (left, right, down for Later) and 1 to 3, each shown where it's used: every answer's
// number on a keycap under its label, the arrows in a line under Later. Each answer is my rating through
// "web", the same change as a tap on a card's rating (Rate.tsx), and Undo undoes that change. The order
// and the areas are deck.ts; this draws it and follows the finger.

// How far a card goes sideways before letting go commits it, as a share of its width, within these bounds
// in px; or a flick (deck.ts, `swipeAnswer`).
const SIDEWAYS = { share: 0.3, min: 80, max: 120 };
// A press that moves less than this is a tap (it opens the card); past it, the card follows the finger.
const SLOP = 8;
// How long "Mei wants this too" stays.
const SAID_MS = 2600;
// How long a card takes to fly off.
const FLY_MS = 300;

// A card answered, flying off from where it was let go (`from`, its transform then).
interface Leave {
  key: number;
  item: Item;
  area: DeckArea;
  tier: Answer | null;
  from: string;
}

// Which way a card leaves for each answer: No to the left, Yes and Maybe to the right (only
// Yes is stamped on it), Later down.
type Way = "left" | "right" | "down";
const wayOf = (tier: Answer | null): Way => (tier === null ? "down" : tier === "no" ? "left" : "right");
const OFF: Record<Way, string> = {
  left: "translate(-140%, 24px) rotate(-18deg)",
  right: "translate(140%, 24px) rotate(18deg)",
  down: "translate(0, 70%) scale(0.9)",
};
// The keys: the arrows as the swipes go (up is nothing, as a swipe up is), down for Later, and each answer's
// number as its button shows it.
const KEYS: Record<string, Answer | null> = {
  ArrowLeft: "no",
  ArrowRight: "yes",
  ArrowDown: null,
  "1": "no",
  "2": "maybe",
  "3": "yes",
};
// What each answer's keys are, as assistive technology hears them.
const SHORTCUTS: Record<Answer, string> = { no: "1 ArrowLeft", maybe: "2", yes: "3 ArrowRight" };

// The deck's run, kept with each step it shows on (router.ts), so Back, Forward and a reload find it as
// it was: started afresh by a step that has none (Start rating, a link to it), else the step's own, unless
// it's this run already (the run here is newer: its answers saved since that step was left). Each answer
// saves as it's given; Undo takes back the last, undoing the change that saved it.
export function useDeck({
  open,
  trip,
  items,
  onChanged,
}: {
  open: boolean;
  trip: TripView;
  items: Item[];
  onChanged: () => void;
}) {
  const you = trip.you;
  const [run, setRun] = useState<DeckRun | null>(null);
  const current = useRef(run);
  current.current = run;
  const [error, setError] = useState<string | null>(null);
  // Each answer's save while it's on its way: Undo waits for it, for the change to undo.
  const pending = useRef(new Map<string, Promise<string | null>>());
  const step = stepState().key;
  const latest = useRef({ items, trip });
  latest.current = { items, trip };
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new step (`step`) is what's looked at
  useLayoutEffect(() => {
    if (!open) return;
    const kept = stepState().deck;
    setError(null);
    setRun((r) => {
      if (isRun(kept)) return r?.id === kept.id ? r : kept;
      const { items, trip } = latest.current;
      return startRun(items, trip.members, trip.you, tripStart(trip.dates));
    });
  }, [open, step]);
  // Kept with the step it shows on, and with the one it comes back to after a card in full answered it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new step (`step`) keeps it too
  useEffect(() => {
    if (open && run) rememberInStep({ deck: run });
  }, [open, run, step]);

  const give = (item: Item, tier: Answer | null) => {
    setError(null);
    setRun((r) => r && answer(r, item.id, tier));
    if (!tier) return;
    const save = api.rate(trip.id, item.id, tier).then((r) => r.change ?? null);
    pending.current.set(item.id, save);
    save.then(
      (change) => {
        setRun((r) => r && saved(r, item.id, change));
        onChanged();
      },
      (e: unknown) => {
        // Taken back already (Undo while it saved): nothing to say.
        if (!current.current?.answers.some((a) => a.id === item.id)) return;
        setRun((r) => r && unanswer(r, item.id));
        setError(`Couldn't save your rating of ${item.title}. ${e instanceof Error ? e.message : "Try again."}`);
      },
    );
    save
      .finally(() => {
        if (pending.current.get(item.id) === save) pending.current.delete(item.id);
      })
      .catch(() => {});
  };
  const undo = async () => {
    const back = run && takeBack(run);
    if (!back) return;
    setError(null);
    setRun(back.run);
    const { last } = back;
    if (!last.tier) return;
    try {
      const change = last.change ?? (await pending.current.get(last.id)?.catch(() => null)) ?? null;
      if (!change) return;
      await api.undo(trip.id, change);
      onChanged();
    } catch (e) {
      setRun((r) => r && giveBack(r, last));
      setError(`Couldn't undo: ${e instanceof Error ? e.message : "try again."}`);
    }
  };
  const goTo = (at: number) => setRun((r) => r && { ...r, at });

  // Cards flying off, each from where it was let go; "Mei wants this too", for a moment; and what screen
  // readers hear of each answer. Kept here, not on the deck drawn, so an answer given on the card in full
  // (which takes the deck's place) plays out on the deck as it comes back.
  const reduced = useMedia("(prefers-reduced-motion: reduce)");
  const [leaving, setLeaving] = useState<Leave[]>([]);
  const leaves = useRef(0);
  const sayings = useRef(0);
  const [said, setSaid] = useState<{ key: number; people: Person[] } | null>(null);
  const [heard, setHeard] = useState("");
  useEffect(() => {
    if (!said) return;
    const timer = setTimeout(() => setSaid(null), SAID_MS);
    return () => clearTimeout(timer);
  }, [said]);
  const left = (key: number) => setLeaving((l) => l.filter((x) => x.key !== key));
  // What's on top now, and its area: the card an answer is for.
  const onTop = (): { item: Item; area: DeckArea } | null => {
    const r = current.current;
    if (!r) return null;
    const { items } = latest.current;
    const shows = deckShows(r, new Map(items.map((i) => [i.id, i])), you);
    const item = shows.kind === "card" ? items.find((i) => i.id === shows.ids[0]) : undefined;
    return item && shows.kind === "card" ? { item, area: shows.area } : null;
  };
  // An answer to the card on top, from a swipe (where it was let go), a button, a key, or its card in full
  // (`onCard`: it flies off from where it sits as the deck comes back). False when that card isn't on top.
  const decide = (tier: Answer | null, from = "none", onCard?: string): boolean => {
    const top = onTop();
    if (!top || (onCard !== undefined && top.item.id !== onCard)) return false;
    if (!reduced) {
      const key = ++leaves.current;
      setLeaving((l) => [...l, { key, item: top.item, area: top.area, tier, from }]);
    }
    const too = wanting(tier) ? wantItToo(top.item, latest.current.trip.members, you) : [];
    setSaid(too.length > 0 ? { key: ++sayings.current, people: too } : null);
    setHeard(
      `${top.item.title}: ${tier ? ratingLabel(tier) : "later"}.${too.length > 0 ? ` ${wantsTooText(too)}.` : ""}`,
    );
    give(top.item, tier);
    return true;
  };
  return { run, error, decide, undo, goTo, you, leaving, left, said, heard, onTop };
}

export type DeckState = ReturnType<typeof useDeck>;

export function Deck({
  deck,
  trip,
  items,
  wide,
  onOpen,
  onClose,
}: {
  deck: DeckState;
  trip: TripView;
  items: Item[];
  wide: boolean;
  // Tapping the card: its card in full, over the deck.
  onOpen: (id: string) => void;
  onClose: () => void;
}) {
  const { run, error, undo, goTo, you, leaving, left, said, heard } = deck;
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const root = useRef<HTMLDivElement>(null);
  // The deck takes the focus as it opens (and as it comes back from a card in full), so the keys work at
  // once and it's read from its start.
  useEffect(() => {
    if (!root.current?.contains(document.activeElement)) root.current?.focus({ preventScroll: true });
  }, []);

  const shows = run ? deckShows(run, byId, you) : null;
  const card = shows?.kind === "card" ? shows : null;
  // An answer to the card on top, from a swipe (where it was let go), a button or a key.
  const decide = (tier: Answer | null, from = "none") => {
    deck.decide(tier, from);
  };
  const decideRef = useRef(decide);
  decideRef.current = decide;

  // The keys, while the deck is what's in front (nothing typed into, no menu open, the map not focused).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A key held down answers once, not card after card.
      if (e.defaultPrevented || e.repeat || e.metaKey || e.ctrlKey || e.altKey || !(e.key in KEYS)) return;
      const target = e.target as HTMLElement | null;
      const inDeck = target === document.body || (target !== null && root.current?.contains(target));
      if (!inDeck || target?.closest("input, textarea, select, [contenteditable]")) return;
      e.preventDefault();
      decideRef.current(KEYS[e.key] ?? null);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  const area = shows && shows.kind !== "nothing" ? shows.area : null;
  const position =
    shows?.kind === "card"
      ? { n: shows.done + 1, total: shows.total, share: shows.done / shows.total }
      : shows?.kind === "summary"
        ? { n: shows.total, total: shows.total, share: 1 }
        : null;
  const width = Math.min(globalThis.innerWidth || 400, 400);
  // "Mei wants this too" as it goes, over the next card. After an area's last, its end says it instead,
  // under "Wanted together".
  const saying = said && (
    <p key={said.key} className="callout tm-deck-said" aria-hidden="true">
      <AvatarStack people={said.people} size={24} max={3} />
      <span>{wantsTooText(said.people)}</span>
    </p>
  );

  return (
    <div className={`tm-deck${wide ? " is-wide" : ""}`} ref={root} tabIndex={-1}>
      <header className="tm-deck-head">
        <div className="tm-deck-row">
          {area && position ? (
            <h2 className="title tm-deck-area">
              <span className="tm-deck-area-name">{area.label}</span>
              <span className="tm-deck-count">{` · ${position.n} of ${position.total}`}</span>
            </h2>
          ) : (
            <span className="tm-deck-area" />
          )}
          {shows?.kind !== "nothing" && (
            <Button
              variant="quiet"
              size="sm"
              disabled={!run || run.answers.length === 0}
              onClick={(e) => {
                haptic("commit", e);
                undo();
              }}
            >
              Undo
            </Button>
          )}
          <button type="button" className="tm-layer-close" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>
        {position && (
          <span className="tm-deck-bar" aria-hidden="true">
            <i style={{ transform: `scaleX(${position.share})` }} />
          </span>
        )}
      </header>

      <div className={`tm-deck-body${card ? " is-cards" : ""}`}>
        {shows?.kind === "card" && (
          <Stage
            key={shows.area.key}
            ids={shows.ids}
            byId={byId}
            area={shows.area}
            trip={trip}
            width={width}
            leaving={leaving}
            onLeft={left}
            onDecide={decide}
            onOpen={onOpen}
            saying={saying}
          />
        )}
        {shows?.kind === "summary" && run && (
          <Summary
            area={shows.area}
            summary={areaSummary(run, shows.area, byId, trip.members, you)}
            next={shows.next}
            trip={trip}
            onNext={() => shows.next && goTo(shows.next.index)}
            onClose={onClose}
          />
        )}
        {shows?.kind === "nothing" && (
          <div className="tm-deck-summary">
            <span className="tm-deck-done-icon">
              <Icon name="check" size={28} />
            </span>
            <h2 className="display-m">Nothing to rate</h2>
            <p className="callout tm-muted">
              Everything in the trip has your rating. What your group adds waits here for yours.
            </p>
            <div className="tm-deck-summary-actions">
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        )}
        {error && (
          <p className="caption tm-error tm-deck-error" role="alert">
            {error}
            <ReportThis />
          </p>
        )}
        {card && (
          <>
            <fieldset className="tm-deck-answers" aria-label="How much do you want this?">
              {ANSWERS.map((tier, n) => (
                <button
                  key={tier}
                  type="button"
                  className={`tm-deck-answer is-${tier}`}
                  title={MEANS[tier]}
                  aria-keyshortcuts={SHORTCUTS[tier]}
                  onClick={(e) => {
                    haptic("commit", e);
                    decide(tier);
                  }}
                >
                  <span className="tm-deck-label">{ratingLabel(tier)}</span>
                  {wide && (
                    <span className="tm-keycap" aria-hidden="true">
                      {n + 1}
                    </span>
                  )}
                </button>
              ))}
            </fieldset>
            <div className="tm-deck-foot">
              <Button
                variant="quiet"
                size="sm"
                aria-keyshortcuts="ArrowDown"
                onClick={(e) => {
                  haptic("commit", e);
                  decide(null);
                }}
              >
                Later
              </Button>
              {wide && (
                <p className="caption tm-muted tm-deck-keys" aria-hidden="true">
                  ← No · → Yes · ↓ Later
                </p>
              )}
            </div>
          </>
        )}
      </div>
      <p className="tm-hidden" role="status">
        {heard}
      </p>
    </div>
  );
}

// The cards: the one on top, which follows the finger, and the next two under it; and those flying off.
function Stage({
  ids,
  byId,
  area,
  trip,
  width,
  leaving,
  onLeft,
  onDecide,
  onOpen,
  saying,
}: {
  ids: string[];
  byId: ReadonlyMap<string, Item>;
  area: DeckArea;
  trip: TripView;
  width: number;
  leaving: Leave[];
  onLeft: (key: number) => void;
  onDecide: (tier: Answer | null, from?: string) => void;
  onOpen: (id: string) => void;
  saying: ReactNode;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLElement>(null);
  const drag = useRef<{
    pointer: number;
    x0: number;
    y0: number;
    dx: number;
    dy: number;
    moving: boolean;
    samples: { t: number; x: number; y: number }[];
    // What letting go would answer by now (by distance), for the tick as that point is crossed.
    counts: Answer | null;
  } | null>(null);
  // A drag just ended on the card: the click that follows isn't a tap.
  const dragged = useRef(false);
  const cards = ids.flatMap((id) => byId.get(id) ?? []);

  // Where the card on top is, as it's dragged: its transform, how far it leans each way (its labels
  // fade in) and how far the next card has come up under it.
  const place = (dx: number, dy: number, settle = false) => {
    const el = topRef.current;
    const st = stage.current;
    if (!el || !st) return;
    // Up or down isn't an answer: the card gives only a little that way, as it does down.
    const y = dy * 0.25;
    el.style.transform = dx === 0 && dy === 0 ? "" : `translate(${dx}px, ${y}px) rotate(${dx / 20}deg)`;
    const { w } = limits(el);
    // Only sideways counts: dragged more up or down than sideways, it leans nowhere.
    const sideways = Math.abs(dx) >= Math.abs(dy);
    el.style.setProperty("--lean-yes", String(sideways && dx > 0 ? Math.min(1, dx / w) : 0));
    el.style.setProperty("--lean-no", String(sideways && dx < 0 ? Math.min(1, -dx / w) : 0));
    const pull = sideways ? Math.min(1, Math.abs(dx) / w) : 0;
    st.style.setProperty("--pull", String(settle ? 0 : pull));
    st.classList.toggle("is-pulling", !settle && pull > 0);
  };

  const down = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // The photo's credit is a link of its own.
    if ((e.target as HTMLElement).closest("a")) return;
    drag.current = {
      pointer: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      dx: 0,
      dy: 0,
      moving: false,
      samples: [],
      counts: null,
    };
    dragged.current = false;
  };
  const move = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    d.dx = e.clientX - d.x0;
    d.dy = e.clientY - d.y0;
    if (!d.moving && Math.hypot(d.dx, d.dy) < SLOP) return;
    if (!d.moving) {
      d.moving = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.classList.add("is-dragging");
    }
    const t = e.timeStamp;
    d.samples = [...d.samples.filter((s) => t - s.t < 100), { t, x: e.clientX, y: e.clientY }];
    place(d.dx, d.dy);
    // A tick as it crosses the point where letting go will count (and again each time it's dragged back
    // and across, or over to another answer), not while it stays past it.
    const counts = swipeAnswer(d.dx, d.dy, limits(e.currentTarget));
    if (counts && counts !== d.counts) haptic("tick", e);
    d.counts = counts;
  };
  const up = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    drag.current = null;
    if (!d.moving) return;
    dragged.current = true;
    e.currentTarget.classList.remove("is-dragging");
    const el = e.currentTarget;
    const [s0] = d.samples;
    const dt = s0 ? e.timeStamp - s0.t : 0;
    const vx = s0 && dt > 0 ? (e.clientX - s0.x) / dt : 0;
    const vy = s0 && dt > 0 ? (e.clientY - s0.y) / dt : 0;
    const from = el.style.transform || "none";
    const tier = swipeAnswer(d.dx, d.dy, limits(el), { vx, vy });
    // Not far or fast enough (or up or down, which aren't answers): back where it was. Answered: it flies
    // off from where it is, and the cards under it settle into their places, the next one on top.
    if (!tier) return place(0, 0, true);
    stage.current?.style.setProperty("--pull", "0");
    stage.current?.classList.remove("is-pulling");
    haptic("commit", e);
    onDecide(tier, from);
  };
  const cancel = (e: ReactPointerEvent<HTMLElement>) => {
    if (!drag.current || drag.current.pointer !== e.pointerId) return;
    drag.current = null;
    e.currentTarget.classList.remove("is-dragging");
    place(0, 0, true);
  };

  return (
    <div className="tm-deck-stage" ref={stage}>
      {[...cards].reverse().map((item) => {
        const at = cards.indexOf(item);
        const isTop = at === 0;
        return (
          <DeckCard
            key={item.id}
            item={item}
            area={area}
            trip={trip}
            width={width}
            className={isTop ? "is-top" : `is-back${at}`}
            {...(isTop
              ? {
                  cardRef: topRef,
                  handlers: {
                    onPointerDown: down,
                    onPointerMove: move,
                    onPointerUp: up,
                    onPointerCancel: cancel,
                  },
                  onOpen: () => {
                    // The end of a drag isn't a tap.
                    if (dragged.current) {
                      dragged.current = false;
                      return;
                    }
                    onOpen(item.id);
                  },
                }
              : {})}
          />
        );
      })}
      {leaving.map((l) => (
        <Leaving key={l.key} leave={l} trip={trip} width={width} onDone={() => onLeft(l.key)} />
      ))}
      {saying}
    </div>
  );
}

// How far the card on top must go sideways before letting go commits it: a share of its own width, not of
// the box it takes as it's dragged and turned (which grows with the turn).
function limits(el: HTMLElement) {
  return { w: Math.max(SIDEWAYS.min, Math.min(SIDEWAYS.max, el.offsetWidth * SIDEWAYS.share)) };
}

// A card answered: it flies off the way its answer goes, from where it was let go, then it's gone.
function Leaving({ leave, trip, width, onDone }: { leave: Leave; trip: TripView; width: number; onDone: () => void }) {
  const ref = useRef<HTMLElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  // biome-ignore lint/correctness/useExhaustiveDependencies: it flies once, as it's drawn
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el?.animate) return done.current();
    const flight = el.animate(
      [
        { transform: leave.from, opacity: 1 },
        { transform: OFF[wayOf(leave.tier)], opacity: 0 },
      ],
      { duration: FLY_MS, easing: "cubic-bezier(0.4, 0, 1, 1)", fill: "forwards" },
    );
    flight.onfinish = () => done.current();
    return () => flight.cancel();
  }, []);
  return (
    <DeckCard
      item={leave.item}
      area={leave.area}
      trip={trip}
      width={width}
      className={`is-leaving${leave.tier === "no" || leave.tier === "yes" ? ` lean-${leave.tier}` : ""}`}
      cardRef={ref}
      style={{ transform: leave.from }}
    />
  );
}

// One card: its photo (else its kind's colour and icon), title, what it is and where, how far from the
// area's stay it's nearest, who added or suggested it, and everyone else's rating with their faces. Tapping the one
// on top opens it in full.
function DeckCard({
  item,
  area,
  trip,
  width,
  className,
  cardRef,
  handlers,
  onOpen,
  style,
}: {
  item: Item;
  area: DeckArea;
  trip: TripView;
  width: number;
  className: string;
  cardRef?: RefObject<HTMLElement | null>;
  handlers?: Pick<HTMLAttributes<HTMLElement>, "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel">;
  onOpen?: () => void;
  style?: CSSProperties;
}) {
  const top = onOpen !== undefined;
  const near = fromStay(item, area);
  const who =
    originLabel(item) ??
    picksLabel(item) ??
    `Added by ${item.created.by.id === trip.you ? "you" : item.created.by.name}`;
  const where = near ? `${km(near.m)} from ${near.title}` : null;
  const others = trip.members.some((m) => m.id !== trip.you && item.interest.some((x) => x.person === m.id && x.tier));
  const byTier = useTight(cardRef);
  return (
    <article
      className={`tm-deck-card ${className}`}
      ref={byTier.ref}
      aria-hidden={top ? undefined : true}
      aria-labelledby={top ? `deck-${item.id}` : undefined}
      style={style}
      {...handlers}
    >
      <span className="tm-deck-photo">
        <ItemCover item={item} trip={trip.id} width={width} you={trip.you} />
        <span className="tm-deck-lean is-yes" aria-hidden="true">
          Yes
        </span>
        <span className="tm-deck-lean is-no" aria-hidden="true">
          No
        </span>
      </span>
      <div className="tm-deck-text">
        <h3 id={top ? `deck-${item.id}` : undefined} className="display-m tm-deck-title">
          {item.title}
        </h3>
        <p className="caption tm-muted tm-deck-line">{itemKind(item)}</p>
        <p className="caption tm-muted tm-deck-line">{factLine([where, who])}</p>
        {others ? (
          <Ratings item={item} members={trip.members} you={trip.you} byTier={byTier.tight} />
        ) : (
          <p className="caption tm-muted">Nobody else has rated it yet</p>
        )}
      </div>
      {top && (
        <button type="button" className="tm-deck-open" aria-label={`${item.title}: open its card`} onClick={onOpen} />
      )}
    </article>
  );
}

// Whether a card's words run past its bottom, its photo already as short as it goes (a 320 by 568 phone, six
// others named one by one): then its ratings go by tier, which always fit. Measured before it's drawn, and
// again, named one by one first, whenever the card changes size. `ref` is the card's, passed on to `cardRef`.
function useTight(cardRef: RefObject<HTMLElement | null> | undefined) {
  const card = useRef<HTMLElement | null>(null);
  const [tight, setTight] = useState(false);
  // After every render: one that names them one by one may need them by tier.
  useLayoutEffect(() => {
    const el = card.current;
    if (el && !tight && el.scrollHeight > el.clientHeight + 1) setTight(true);
  });
  useEffect(() => {
    const el = card.current;
    if (!el) return;
    let height = el.clientHeight;
    const watch = new ResizeObserver(() => {
      if (el.clientHeight === height) return;
      height = el.clientHeight;
      // Named one by one again to be measured (below), or measured now when they already are.
      setTight((was) => (was ? false : el.scrollHeight > el.clientHeight + 1));
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  const ref = useCallback(
    (el: HTMLElement | null) => {
      card.current = el;
      if (cardRef) cardRef.current = el;
    },
    [cardRef],
  );
  return { tight, ref };
}

// At an area's end: what I rated, by tier, and how many I left for later; what we both want; and the next
// area, or, after the last, that that's everything.
function Summary({
  area,
  summary,
  next,
  trip,
  onNext,
  onClose,
}: {
  area: DeckArea;
  summary: AreaSummary;
  next: { area: DeckArea; left: number } | null;
  trip: TripView;
  onNext: () => void;
  onClose: () => void;
}) {
  const { rated, later, together } = summary;
  const ref = useRef<HTMLElement>(null);
  // The way on takes the focus, so Enter goes on.
  useEffect(() => ref.current?.querySelector<HTMLElement>(".sv-btn--primary")?.focus({ preventScroll: true }), []);
  return (
    <section className="tm-deck-summary" aria-labelledby="deck-done" ref={ref}>
      <span className="tm-deck-done-icon">
        <Icon name="check" size={28} />
      </span>
      <h2 id="deck-done" className="display-m">
        {`${area.label}, done`}
      </h2>
      {(rated.length > 0 || later > 0) && (
        <ul className="tm-deck-tally" aria-label="What you rated">
          {rated.map((r) => (
            <li key={r.tier} className={`caption tm-deck-tallied is-${r.tier}`}>
              {`${r.n} ${ratingLabel(r.tier)}`}
            </li>
          ))}
          {later > 0 && <li className="caption tm-deck-tallied">{`${later} for later`}</li>}
        </ul>
      )}
      {together.length > 0 && (
        <section className="tm-deck-together" aria-labelledby="deck-together">
          <h3 id="deck-together" className="eyebrow tm-muted">
            Wanted together
          </h3>
          <ul className="tm-rows">
            {together.map(({ item, people }) => (
              <li key={item.id} className="tm-deck-together-row">
                <ItemThumb item={item} trip={trip.id} />
                <span className="tm-row-text">
                  <span className="callout">{item.title}</span>
                  <span className="caption tm-muted">{`${namesOf(people)} too`}</span>
                </span>
                <AvatarStack people={people} size={24} max={3} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {!next && (
        <p className="callout tm-muted">
          {later > 0
            ? "That's everything for now. What you left for later waits on Ideas, and here next time."
            : "That's everything waiting on you for now."}
        </p>
      )}
      <div className="tm-deck-summary-actions">
        {next ? (
          <>
            <Button variant="primary" onClick={onNext}>
              {`Next: ${next.area.label} · ${next.left}`}
            </Button>
            <Button variant="quiet" onClick={onClose}>
              Done for now
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        )}
      </div>
    </section>
  );
}

const namesOf = (people: Person[]) =>
  people.length <= 2
    ? people.map((p) => p.name).join(" and ")
    : `${people[0]?.name}, ${people[1]?.name} and ${people.length - 2} more`;
